"use client";
import { useGSAP } from "@gsap/react";
import { RelationBlock } from "../../../sanity/lib/types/relationBlock";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useIsMobile from "../../hooks/useIsMobile";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

const splitIntoColumns = (list: Array<{ title: string }>, columnCount: number) => {
    const itemsPerColumn = Math.ceil(list.length / columnCount);
    return Array.from({ length: columnCount }, (_, colIndex) =>
        list.slice(colIndex * itemsPerColumn, (colIndex + 1) * itemsPerColumn),
    );
};

export const RelationBlockModule = ({ relationBlock }: { relationBlock: RelationBlock }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const observer = useRef<IntersectionObserver>(null);
    const [isVisible, setIsVisible] = useState(false);
    const { isMobile } = useIsMobile();

    useGSAP(
        () => {
            gsap.set(".gsap-headline", { y: 50, opacity: 0 });
        },
        { scope: containerRef, dependencies: [isMobile] },
    );

    useEffect(() => {
        if (isVisible) {
            gsap.to(".gsap-headline", {
                y: 0,
                opacity: 1,
                stagger: 0.1,
            });
        }
    }, [isVisible, isMobile]);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: "0px",
            threshold: 0.2,
        };

        observer.current = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            observerOptions,
        );
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            observer.current?.observe(containerRef.current);
        }
        return () => observer.current?.disconnect();
    }, []);

    return (
        <section ref={containerRef} className="container">
            <div className="mx-2.5 md:mx-5 py-20 md:py-28 relation-block-module">
                <h2 className="mb-6 md:mb-12 m-headline-1 md:d-headline-1 gsap-headline">
                    {relationBlock.title}
                </h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2.5 md:gap-x-6">
                    {splitIntoColumns(relationBlock.relations || [], isMobile ? 2 : 4)?.map(
                        (column, colIndex) => (
                            <div className="col-span-1" key={colIndex}>
                                {column.map((relation, index) => (
                                    <h3 key={index} className="m-body-l md:d-body-l relation-items">
                                        {relation.title}
                                    </h3>
                                ))}
                            </div>
                        ),
                    )}
                </div>
            </div>
        </section>
    );
};
